import { QueryClient, useMutation } from "@tanstack/react-query"
import { KeycollectionType, ObjectType } from "../types/types"
import {
    addImageToObject,
    addKeycollection,
    createObjectServiceContract,
    deleteDocFromObject,
    deleteKeycollection,
    generateQRCodeOfObject,
    postNewDocToObject,
    updateKeycollection,
    updateListOfServicesOfObject,
    updateObject,
} from "./apiCalls"
import { useContext, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { messageError, messagePending, messageSuccess } from "../../../services/messages"
import { apiErrorMessage } from "../../../services/apiErrorText"

// update object data
export const useUpdateObjectMutation = (queryClient: QueryClient) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; body: ObjectType }>({
        mutationFn: updateObject,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Objekt wird gerade aktualisiert.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Das Objekt wurde erfolgreich aktualisiert.")
            queryClient.invalidateQueries({
                queryKey: ["singleObjectData" + variables.id],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Objekt konnte nicht aktualisiert werden."))
            console.log(err)
        },
    })

    return mutation
}

// update list of services of object
export const useUpdateListOfServiesOfObjectMutation = (queryClient: QueryClient, modalConId: number) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const modalCon = useContext(ModalContext)
    const mutation = useMutation<
        unknown,
        Error,
        {
            id: string
            serviceList: any
        }
    >({
        mutationFn: updateListOfServicesOfObject,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Leistungsverzeichnis wird gerade gespeichert.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Das Leistungsverzeichnis wurde erfolgreich gespeichert.")
            modalCon?.trigger(modalConId)
            queryClient.invalidateQueries({
                queryKey: ["singleObjectData" + variables.id],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Leistungsverzeichnis konnte nicht gespeichert werden."))
            console.log(err)
        },
    })
    return mutation
}

// create service object contract
export const useCreateObjectContractMutation = (queryClient: QueryClient, modalConId: number) => {
    const modalCon = useContext(ModalContext)
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<
        unknown,
        Error,
        {
            obj: string
            cus: string
            contract_start: string
            price: number
        }
    >({
        mutationFn: createObjectServiceContract,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Objektbetreuungsvertrag wird gerade erstellt.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Der Objektbetreuungsvertrag wurde erfolgreich erstellt.")
            modalCon?.trigger(modalConId)
            queryClient.invalidateQueries({
                queryKey: ["singleObjectData" + variables.obj],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Objektbetreuungsvertrag konnte nicht erstellt werden."))
            console.log(err)
        },
    })

    return mutation
}

// upload document(s) to object
export const useAddDocToObjectMutation = (queryClient: QueryClient) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; data: any; path?: string }>({
        mutationFn: postNewDocToObject,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Dokument wird gerade hinzugefügt.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Das Dokument wurde erfolgreich hinzugefügt.")
            queryClient.invalidateQueries({
                queryKey: ["singleObjectData" + variables.id],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Dokument konnte nicht hinzugefügt werden."))
            console.log(err)
        },
    })

    return mutation
}

// delete doc-data from object & from s3
export const usePatchDocObjectMutation = (queryClient: QueryClient, modalCon: any, modalConId: number) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; file: string }>({
        mutationFn: deleteDocFromObject,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Dokument wird gerade gelöscht.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Das Dokument wurde erfolgreich gelöscht.")
            modalCon?.trigger(modalConId)
            queryClient.invalidateQueries({
                queryKey: ["singleObjectData" + variables.id],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Dokument konnte nicht gelöscht werden."))
            console.log(err)
        },
    })

    return mutation
}

// NOTE still in progress
// upload image - general?
export const useAddImageToObjectMutation = (queryClient: QueryClient, setLoading: (e: boolean) => void) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; img: FormData }>({
        mutationFn: addImageToObject,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Upload wird gerade durchgeführt.")
        },
        onSuccess: (data, variables) => {
            setLoading(false)
            messageSuccess(notificationKey, "Fertig!", "Upload erfolgreich durchgeführt.")
            queryClient.invalidateQueries({
                queryKey: ["singleObjectImage" + variables.id],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Upload ist fehlgeschlagen."))
            setLoading(false)
            console.log(err)
        },
    })

    return mutation
}

// generate qr code
export const useGenerateQRCodeOfObjectMutation = (queryClient: QueryClient) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    return useMutation<unknown, Error, { id: string }>({
        mutationFn: generateQRCodeOfObject,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der QR-Code wird gerade erstellt.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "QR Code erfolgreich generiert.")
            queryClient.invalidateQueries({
                queryKey: ["singleObjectData" + variables.id],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Generierung des QR Codes ist fehlgeschlagen."))
            console.log(err)
        },
    })
}

// add keycollection
export const useAddKeycollectionMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { body: KeycollectionType; objectId: string }>({
        mutationFn: (variables) => addKeycollection(variables.body, variables.objectId),
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Schlüsselbund wird gerade erstellt.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Der Schlüsselbund wurde erfolgreich erstellt.")
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Schlüsselbund konnte nicht erstellt werden."))
            console.log(err)
        },
    })
    return mutation
}

// update keycollection
export const useUpdateKeycollectionMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { body: KeycollectionType; objectId: string; keycollectionId: string }>({
        mutationFn: (variables) => updateKeycollection(variables.body, variables.objectId, variables.keycollectionId),
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Schlüsselbund wird gerade aktualisiert.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Der Schlüsselbund wurde erfolgreich aktualisiert.")
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Schlüsselbund konnte nicht aktualisiert werden."))
            console.log(err)
        },
    })
    return mutation
}

// delete keycollection
export const useDeleteKeycollectionMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { objectId: string; keycollectionId: string }>({
        mutationFn: (variables) => deleteKeycollection(variables.objectId, variables.keycollectionId),
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Schlüsselbund wird gerade gelöscht.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Der Schlüsselbund wurde erfolgreich gelöscht.")
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Schlüsselbund konnte nicht gelöscht werden."))
            console.log(err)
        },
    })
    return mutation
}
